import { useCallback, useState } from 'preact/hooks';

/**
 * A single on/off display preference, persisted in `localStorage`.
 *
 * Deliberately not in the repository: this is how the app is *displayed*, not the
 * instructor's data, so it has no business in the schedule that Phase 2 would sync
 * across devices — the right setting on a phone may be the wrong one on a tablet.
 *
 * Reads and writes are wrapped because `localStorage` throws in private-mode
 * Safari, and losing a display preference must never break the screen.
 */
export function usePreference(
  key: string,
  fallback: boolean,
): readonly [boolean, (value: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored === null ? fallback : stored === 'true';
    } catch {
      return fallback;
    }
  });

  const update = useCallback(
    (next: boolean) => {
      setValue(next);
      try {
        localStorage.setItem(key, String(next));
      } catch {
        // A preference that cannot be saved still applies for this session.
      }
    },
    [key],
  );

  return [value, update];
}

/** Whether the schedule list shows days with nothing on them. Off by default. */
export const SHOW_EMPTY_DAYS_KEY = 'yoga.showEmptyDays';
